import type { HttpInterceptorFn } from '@angular/common/http';
import type { JwtTokens } from '@wizbii-utils/angular/jwt';

import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { JwtRefreshStateService } from '@core/services/jwt/jwt-refresh-state.service';
import { JWT_SERVICE_TOKEN } from '@wizbii-utils/angular/jwt';
import { throwError } from 'rxjs';
import { catchError, filter, finalize, first, switchMap, take } from 'rxjs/operators';

export const jwtRefreshInterceptorFn: HttpInterceptorFn = (request, next) => {
  const jwtService = inject(JWT_SERVICE_TOKEN);
  const refreshState = inject(JwtRefreshStateService);
  const jwtHelper = new JwtHelperService();

  const addToken = (token: string) => request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

  const logoutUser = (error?: unknown) => {
    jwtService.logout();
    return throwError(() => error ?? 'LOGOUT');
  };

  const handle401Error = (tokensLocal: JwtTokens) => {
    if (!refreshState.isRefreshing) {
      refreshState.isRefreshing = true;
      refreshState.tokenSubject.next(null);

      return jwtService.refreshToken(tokensLocal).pipe(
        switchMap((newTokens) => {
          if (newTokens) {
            refreshState.tokenSubject.next(newTokens);
            jwtService.saveLocalTokens(newTokens);
            return next(addToken(newTokens.token));
          }
          return logoutUser();
        }),
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) {
            return logoutUser(error);
          }
          return throwError(() => error);
        }),
        finalize(() => {
          refreshState.isRefreshing = false;
        })
      );
    }

    return refreshState.tokenSubject.pipe(
      filter((tokens): tokens is JwtTokens => tokens !== null),
      take(1),
      switchMap((tokens) => next(addToken(tokens.token)))
    );
  };

  return jwtService.getTokens().pipe(
    first(),
    switchMap((tokens) => {
      const isBlacklisted = jwtService.blacklistRoutes.some((route) =>
        route instanceof RegExp ? route.test(request.url) : request.url.includes(route)
      );

      if (!tokens || isBlacklisted) {
        return next(request);
      }

      if (jwtHelper.isTokenExpired(tokens.token)) {
        return handle401Error(tokens);
      }

      return next(addToken(tokens.token)).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return handle401Error(tokens);
          }
          return throwError(() => error);
        })
      );
    })
  );
};
